/**
 * Real DWI/PWI raw-data preparation: 4D DWI->(b0,DWI) order detection and
 * ADC calculation from DWI+b0, ported verbatim from
 * `openads-wasm/source/src/ads/services/preprocessing/volume_order_detector.py`
 * and `.../adc_calculator.py` (both read in full, not re-derived). Simple
 * deterministic numpy arithmetic + a brightness/gradient-variance heuristic —
 * not a spatial clustering algorithm like SLIC, so a direct TS port
 * (validated numerically against the real Python below) is the right tool
 * here, not Pyodide.
 */
import type { NiftiVolume } from "./nifti";

type Dims3 = [number, number, number];

/** `np.gradient(vol)` magnitude, matching numpy's central-difference-with-
 *  one-sided-boundary convention exactly, on an F-order (x fastest) 3D array. */
function gradientMagnitude(vol: Float32Array, dims: Dims3): Float32Array {
  const [nx, ny, nz] = dims;
  const idx = (x: number, y: number, z: number) => x + y * nx + z * nx * ny;
  const out = new Float32Array(vol.length);
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      for (let x = 0; x < nx; x++) {
        const gx =
          nx < 2 ? 0 : x === 0 ? vol[idx(1, y, z)] - vol[idx(0, y, z)] : x === nx - 1 ? vol[idx(nx - 1, y, z)] - vol[idx(nx - 2, y, z)] : (vol[idx(x + 1, y, z)] - vol[idx(x - 1, y, z)]) / 2;
        const gy =
          ny < 2 ? 0 : y === 0 ? vol[idx(x, 1, z)] - vol[idx(x, 0, z)] : y === ny - 1 ? vol[idx(x, ny - 1, z)] - vol[idx(x, ny - 2, z)] : (vol[idx(x, y + 1, z)] - vol[idx(x, y - 1, z)]) / 2;
        const gz =
          nz < 2 ? 0 : z === 0 ? vol[idx(x, y, 1)] - vol[idx(x, y, 0)] : z === nz - 1 ? vol[idx(x, y, nz - 1)] - vol[idx(x, y, nz - 2)] : (vol[idx(x, y, z + 1)] - vol[idx(x, y, z - 1)]) / 2;
        out[idx(x, y, z)] = Math.sqrt(gx * gx + gy * gy + gz * gz);
      }
    }
  }
  return out;
}

/** `np.percentile(a, p)`, linear-interpolation method (numpy's default). */
function percentile(sorted: Float32Array, p: number): number {
  if (sorted.length === 0) return 0;
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const s = Float64Array.from(values).sort();
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function variance(values: Float32Array | number[]): number {
  const n = values.length;
  if (n === 0) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) sum += values[i];
  const mean = sum / n;
  let sq = 0;
  for (let i = 0; i < n; i++) sq += (values[i] - mean) * (values[i] - mean);
  return sq / n;
}

/**
 * Real `VolumeOrderDetector.is_order_swapped` (volume_order_detector.py:44-101):
 * B0 should be brighter (median) and DWI should have higher gradient
 * variance (sharper edges). Two-vote system, >=2 votes (brightness_weight=1.0
 * + gradient_vote=1.0, votes_threshold=2) means "swapped".
 */
export function isOrderSwapped(vol0: Float32Array, vol1: Float32Array, dims: Dims3): boolean {
  const n = vol0.length;
  const vAvg = new Float32Array(n);
  for (let i = 0; i < n; i++) vAvg[i] = (vol0[i] + vol1[i]) / 2;

  const nz: number[] = [];
  for (let i = 0; i < n; i++) if (vAvg[i] > 0) nz.push(vAvg[i]);
  if (nz.length === 0) return false; // real code: "no non-zero voxels, assuming correct order"

  const sortedNz = Float32Array.from(nz).sort();
  const lo = percentile(sortedNz, 20.0);
  const hi = percentile(sortedNz, 99.9);

  const mask = new Uint8Array(n);
  let maskAny = false;
  for (let i = 0; i < n; i++) {
    if (vAvg[i] > lo && vAvg[i] < hi) {
      mask[i] = 1;
      maskAny = true;
    }
  }

  const vals0: number[] = [];
  const vals1: number[] = [];
  for (let i = 0; i < n; i++) {
    if (!maskAny || mask[i]) {
      vals0.push(vol0[i]);
      vals1.push(vol1[i]);
    }
  }
  const med0 = median(vals0);
  const med1 = median(vals1);
  const brightnessVote = med0 < med1;

  const g0 = gradientMagnitude(vol0, dims);
  const g1 = gradientMagnitude(vol1, dims);
  const gv0vals: number[] = [];
  const gv1vals: number[] = [];
  for (let i = 0; i < n; i++) {
    if (!maskAny || mask[i]) {
      gv0vals.push(g0[i]);
      gv1vals.push(g1[i]);
    }
  }
  const gv0 = variance(gv0vals);
  const gv1 = variance(gv1vals);
  const GRADIENT_WEIGHT = 0.9;
  const gradientVote = gv0 > GRADIENT_WEIGHT * gv1;

  const BRIGHTNESS_WEIGHT = 1.0;
  const votes = (brightnessVote ? BRIGHTNESS_WEIGHT : 0) + (gradientVote ? 1 : 0);
  return votes >= 2;
}

/** Real `VolumeOrderDetector.detect_and_split` — returns [b0, dwi] in correct order. */
export function detectAndSplitVolumeOrder(vol0: Float32Array, vol1: Float32Array, dims: Dims3): [Float32Array, Float32Array] {
  return isOrderSwapped(vol0, vol1, dims) ? [vol1, vol0] : [vol0, vol1];
}

/**
 * Real `ADCCalculator.compute_from_images` (adc_calculator.py:39-82):
 * ADC = -ln((DWI+eps)/(B0+eps)) / bvalue, only where B0>0 and DWI>0,
 * zero elsewhere. Defaults: bvalue=1000.0, epsilon=1e-10.
 */
export function computeAdc(dwiVoxels: Float32Array, b0Voxels: Float32Array, bvalue = 1000.0, epsilon = 1e-10): Float32Array {
  const n = dwiVoxels.length;
  const adc = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const d = dwiVoxels[i];
    const b = b0Voxels[i];
    if (b > 0 && d > 0) {
      adc[i] = -Math.log((d + epsilon) / (b + epsilon)) / bvalue;
    }
  }
  return adc;
}

export interface PreparedDwiRaw {
  dwi: NiftiVolume;
  adc: NiftiVolume;
  b0: NiftiVolume | null;
  /** True if order detection found the DWI/b0 pair swapped and corrected it. */
  orderWasSwapped: boolean;
  /** True if ADC was computed here rather than found as a separate input file. */
  adcWasComputed: boolean;
}

/**
 * Real `RawDataProcessor.run()` (preprocessing_dwi_prepare.py:115-165)
 * orchestration, ported: if an ADC file already exists, use it as-is (no
 * recompute) — this is the path the bundled demo subject takes, a no-op
 * matching current behavior exactly. Only when no ADC file is given does
 * this compute one from DWI+b0 (splitting a 4D DWI/b0 pair first, with order
 * detection, if a separate b0 wasn't given either).
 */
export function prepareRawDwiData(
  dwi3dOrFirstOfPair: NiftiVolume,
  b0SecondOfPairIfAny: NiftiVolume | null,
  existingAdc: NiftiVolume | null,
  existingB0: NiftiVolume | null,
  bvalue = 1000.0,
): PreparedDwiRaw {
  if (existingAdc) {
    // Real code: "ADC file found... Using existing ADC file" — no recompute.
    return { dwi: dwi3dOrFirstOfPair, adc: existingAdc, b0: existingB0 ?? b0SecondOfPairIfAny, orderWasSwapped: false, adcWasComputed: false };
  }

  let dwiVol = dwi3dOrFirstOfPair;
  let b0Vol = existingB0 ?? b0SecondOfPairIfAny;
  let orderWasSwapped = false;

  if (b0SecondOfPairIfAny && !existingB0) {
    // A 4D series was split into two candidate volumes upstream; this is the
    // (vol0, vol1) = (dwi3dOrFirstOfPair, b0SecondOfPairIfAny) order-detection case.
    orderWasSwapped = isOrderSwapped(dwi3dOrFirstOfPair.voxels, b0SecondOfPairIfAny.voxels, dwi3dOrFirstOfPair.dims);
    const [b0Data, dwiData] = detectAndSplitVolumeOrder(dwi3dOrFirstOfPair.voxels, b0SecondOfPairIfAny.voxels, dwi3dOrFirstOfPair.dims);
    dwiVol = { ...dwi3dOrFirstOfPair, voxels: dwiData };
    b0Vol = { ...dwi3dOrFirstOfPair, voxels: b0Data };
  }

  if (!b0Vol) {
    throw new Error("Cannot compute ADC: no b0 volume available and no ADC file found (matches real RawDataProcessor's FileNotFoundError).");
  }

  const adcVoxels = computeAdc(dwiVol.voxels, b0Vol.voxels, bvalue);
  const adc: NiftiVolume = { ...dwiVol, voxels: adcVoxels };
  return { dwi: dwiVol, adc, b0: b0Vol, orderWasSwapped, adcWasComputed: true };
}
